/**
 * SyndicateSummary Component
 *
 * Shows collective conclusions and committed amounts in syndicate mode.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Users, TrendingUp, ThumbsDown, Eye, ThumbsUp, Handshake } from 'lucide-react-native';
import Animated, { FadeIn } from 'react-native-reanimated';

import { useThemeColors } from '@/lib/useThemeColors';
import type { DDCollaborator } from '@/lib/shared-dd';
import { getSyndicateSummary, CONCLUSION_CONFIG } from '@/lib/shared-dd';

// ============================================================================
// Types
// ============================================================================

interface SyndicateSummaryProps {
  collaborators: DDCollaborator[];
}

// ============================================================================
// Component
// ============================================================================

export function SyndicateSummary({ collaborators }: SyndicateSummaryProps) {
  const colors = useThemeColors();
  const summary = getSyndicateSummary(collaborators);

  const stats = [
    {
      label: 'Pass',
      value: summary.passed,
      icon: ThumbsDown,
      color: colors.text.tertiary,
      bg: colors.surface.secondary,
    },
    {
      label: 'Watching',
      value: summary.watching,
      icon: Eye,
      color: colors.info,
      bg: colors.primary[50],
    },
    {
      label: 'Interested',
      value: summary.interested,
      icon: ThumbsUp,
      color: colors.success,
      bg: colors.traffic.greenLight,
    },
    {
      label: 'Committed',
      value: summary.committed,
      icon: Handshake,
      color: colors.secondary[600],
      bg: colors.secondary[50],
    },
  ];

  return (
    <Animated.View entering={FadeIn.duration(300)}>
      <View
        style={[
          styles.container,
          { backgroundColor: colors.surface.primary, borderColor: colors.border.light },
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <Users size={18} color={colors.primary[500]} />
            <Text style={[styles.title, { color: colors.text.primary }]}>
              Syndicate Status
            </Text>
          </View>
          <View
            style={[styles.countBadge, { backgroundColor: colors.primary[50] }]}
          >
            <Text style={[styles.countText, { color: colors.primary[600] }]}>
              {summary.total} investors
            </Text>
          </View>
        </View>

        {/* Stats Grid */}
        <View style={styles.statsGrid}>
          {stats.map((stat) => (
            <View
              key={stat.label}
              style={[styles.statBox, { backgroundColor: stat.bg }]}
            >
              <stat.icon size={16} color={stat.color} />
              <Text style={[styles.statValue, { color: stat.color }]}>
                {stat.value}
              </Text>
              <Text style={[styles.statLabel, { color: colors.text.tertiary }]}>
                {stat.label}
              </Text>
            </View>
          ))}
        </View>

        {/* Total Committed */}
        {summary.totalCommitted > 0 && (
          <View
            style={[
              styles.totalRow,
              { backgroundColor: colors.traffic.greenLight },
            ]}
          >
            <TrendingUp size={16} color={colors.success} />
            <Text style={[styles.totalLabel, { color: colors.success }]}>
              Total Committed
            </Text>
            <Text style={[styles.totalValue, { color: colors.success }]}>
              CHF {summary.totalCommitted.toLocaleString()}
            </Text>
          </View>
        )}
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    borderWidth: 1,
    padding: 16,
    gap: 14,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
  },
  countBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 8,
  },
  countText: {
    fontSize: 12,
    fontWeight: '600',
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 8,
  },
  statBox: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    gap: 4,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 11,
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderRadius: 10,
  },
  totalLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  totalValue: {
    fontSize: 15,
    fontWeight: '700',
  },
});
